import {
  computePipeSectionMAWP,
  computePipeSectionThicknessFromMAWP,
} from 'features/indicationTriage/cards/ffsCalc/calculations/mawp/calculatePipingMawp'
import {
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValue } from 'features/libra/ontology/ontologyFieldTypes'
import { StepValidationResult } from 'features/libra/workflow/workflowStepsApi'
import {
  GeckoComponentModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

const WORKING_UNIT_RATIO = GeckoMeasurementUnit.Ratio

const PRESSURE_FIELD = 'designPressure'
const YIELD_FIELD = 'yieldStrength'
const QUALITY_FACTOR_FIELD = 'qualityFactor'
const PRESSURE_COEFFICIENT_FIELD = 'pressureCoefficient'
const TSL_FIELD = 'tsl'

interface ComputeMinThicknessValidationArgs {
  /**
   * Typed asset-model component (e.g. `GeckoPipeSection`,
   * `GeckoPressureVesselShell`). Authoritative source for every intrinsic
   * input: geometry (`radius`, `wallNominalThickness`) plus the operating
   * params `designPressure` (P), `yieldStrength` (S_y), `jointEfficiency` (E),
   * `pressureCoefficient` (Y) and `tsl`. Comes from
   * `useFfsTicketContext().componentModel` (the ticket's asset-model snapshot).
   */
  componentModel: GeckoComponentModel | undefined
  /**
   * Legacy fallback only. Tickets created before the Data Gathering step was
   * retired stored the operating params here (`designPressure`,
   * `yieldStrength`, `qualityFactor`, `pressureCoefficient`, `tsl`); we read
   * them iff the asset-model snapshot doesn't carry the value. New tickets get
   * everything from `componentModel`.
   */
  fieldValues: Record<string, unknown> | undefined
}

export interface MinimumThicknessInputs {
  outerDiameter: GeckoMeasurement | null
  nominalThickness: GeckoMeasurement | null
  designPressure: GeckoMeasurement | null
  yieldStrength: GeckoMeasurement | null
  qualityFactor: GeckoMeasurement | null
  pressureCoefficient: GeckoMeasurement | null
  tsl: GeckoMeasurement | null
  /** Raw numbers for substituted-formula display, in working units. */
  raw: {
    D_mm: number | null
    tNom_mm: number | null
    P_MPa: number | null
    Sy_MPa: number | null
    E: number | null
    Y: number | null
    tsl_mm: number | null
  }
}

export interface MinimumThicknessResult {
  tMinC: GeckoMeasurement | null
  tMinL: GeckoMeasurement | null
  tMin: GeckoMeasurement | null
  mawpC: GeckoMeasurement | null
  mawpL: GeckoMeasurement | null
  mawp: GeckoMeasurement | null
}

interface ComputeMinThicknessValidationResult {
  validation: StepValidationResult
  inputs: MinimumThicknessInputs
  results: MinimumThicknessResult
  missingInputs: ReadonlyArray<string>
}

const readNumber = (
  fieldValues: Record<string, unknown>,
  key: string
): number | null => {
  const raw = unwrapFieldValue(fieldValues[key] as FieldValue | undefined)
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

/** Narrow a plain-number asset-model field (E, Y) to a finite value or null. */
const finiteOrNull = (n: number | undefined): number | null =>
  typeof n === 'number' && Number.isFinite(n) ? n : null

const toMeas = (
  num: number | null,
  unit: GeckoMeasurementUnit
): GeckoMeasurement | null =>
  num === null ? null : GeckoMeasurement.CreateFromValue(num, unit)

const measToNumber = (
  meas: GeckoMeasurement | undefined,
  unit: GeckoMeasurementUnit
): number | null => (meas ? meas.convertTo(unit).num : null)

/**
 * Validates and computes the Minimum Thickness Analysis step:
 *
 *   t_min^C = (P * D) / (2 * S_y * E + 2 * P * Y)
 *   t_min^L = (P * D) / (4 * S_y * E + 4 * P * Y) + t_sl
 *   MAWP^C  = (2 * S_y * E * t_nom) / (D - 2 * Y * t_nom)
 *   MAWP^L  = (4 * S_y * E * (t_nom - t_sl)) / (D - 4 * Y * (t_nom - t_sl))
 *
 * Reuses the pure functions from
 * `features/indicationTriage/cards/ffsCalc/calculations/mawp/calculatePipingMawp`.
 * All inputs — geometry (D, t_nom) AND the operating params (P, S_y, E, Y,
 * t_sl) — come from the typed asset-model `componentModel` (the ticket's
 * snapshot); `fieldValues` is consulted only as a legacy fallback for tickets
 * created before the Data Gathering step was retired. Both are passed in by the
 * step component — this is a plain function. The caller wraps the call in
 * `useMemo` for React-side memoization.
 */
export function computeMinThicknessValidation({
  componentModel,
  fieldValues,
}: ComputeMinThicknessValidationArgs): ComputeMinThicknessValidationResult {
  const fvRecord = (fieldValues ?? {}) as Record<string, unknown>

  // All intrinsic inputs come off the typed asset-model component. `radius`,
  // `wallNominalThickness`, `designPressure`, `yieldStrength` and `tsl` are
  // `GeckoMeasurement` instances (convert to working units); `jointEfficiency`
  // (E) and `pressureCoefficient` (Y) are plain ratios.
  const cm = componentModel as
    | {
        radius?: GeckoMeasurement
        wallNominalThickness?: GeckoMeasurement
        designPressure?: GeckoMeasurement
        yieldStrength?: GeckoMeasurement
        jointEfficiency?: number
        pressureCoefficient?: number
        tsl?: GeckoMeasurement
      }
    | undefined
  const radiusMm = measToNumber(cm?.radius, WORKING_UNIT_THICKNESS)
  const D_mm = radiusMm !== null ? radiusMm * 2 : null
  const tNom_mm = measToNumber(cm?.wallNominalThickness, WORKING_UNIT_THICKNESS)

  // Operating params: asset-model snapshot first (the post-cutover home), then
  // legacy ticket `fieldValues` for pre-cutover tickets. `??` keeps a real 0
  // from the snapshot rather than falling through to the legacy value.
  const P_MPa =
    measToNumber(cm?.designPressure, WORKING_UNIT_PRESSURE) ??
    readNumber(fvRecord, PRESSURE_FIELD)
  const Sy_MPa =
    measToNumber(cm?.yieldStrength, WORKING_UNIT_PRESSURE) ??
    readNumber(fvRecord, YIELD_FIELD)
  const E =
    finiteOrNull(cm?.jointEfficiency) ??
    readNumber(fvRecord, QUALITY_FACTOR_FIELD)
  const Y =
    finiteOrNull(cm?.pressureCoefficient) ??
    readNumber(fvRecord, PRESSURE_COEFFICIENT_FIELD)
  const tsl_mm =
    measToNumber(cm?.tsl, WORKING_UNIT_THICKNESS) ??
    readNumber(fvRecord, TSL_FIELD)

  const inputs: MinimumThicknessInputs = {
    outerDiameter: toMeas(D_mm, WORKING_UNIT_THICKNESS),
    nominalThickness: toMeas(tNom_mm, WORKING_UNIT_THICKNESS),
    designPressure: toMeas(P_MPa, WORKING_UNIT_PRESSURE),
    yieldStrength: toMeas(Sy_MPa, WORKING_UNIT_PRESSURE),
    qualityFactor: toMeas(E, WORKING_UNIT_RATIO),
    pressureCoefficient: toMeas(Y, WORKING_UNIT_RATIO),
    tsl: toMeas(tsl_mm, WORKING_UNIT_THICKNESS),
    raw: { D_mm, tNom_mm, P_MPa, Sy_MPa, E, Y, tsl_mm },
  }

  const missingInputs: string[] = []
  if (!componentModel)
    missingInputs.push(
      'Asset Model -- component not yet added to the asset model'
    )
  if (componentModel && D_mm === null)
    missingInputs.push('Outer diameter (Asset Model)')
  if (componentModel && tNom_mm === null)
    missingInputs.push('Nominal wall thickness (Asset Model)')
  if (P_MPa === null) missingInputs.push('Design Pressure (Asset Model)')
  if (Sy_MPa === null) missingInputs.push('Yield Strength (Asset Model)')
  if (E === null) missingInputs.push('Joint Efficiency (Asset Model)')
  if (Y === null) missingInputs.push('Pressure Coefficient (Asset Model)')
  if (tsl_mm === null)
    missingInputs.push('Thickness for Supplemental Loads (Asset Model)')

  const ready = missingInputs.length === 0

  // Compute t_min (inverse of MAWP given design pressure).
  const tMinC = ready
    ? computePipeSectionThicknessFromMAWP({
        mawp: inputs.designPressure,
        yieldStrength: inputs.yieldStrength,
        qualityFactor: inputs.qualityFactor,
        outerDiameter: inputs.outerDiameter,
        pressureCoefficient: inputs.pressureCoefficient,
        tsl: inputs.tsl,
        axis: 'x',
      })
    : null
  const tMinL = ready
    ? computePipeSectionThicknessFromMAWP({
        mawp: inputs.designPressure,
        yieldStrength: inputs.yieldStrength,
        qualityFactor: inputs.qualityFactor,
        outerDiameter: inputs.outerDiameter,
        pressureCoefficient: inputs.pressureCoefficient,
        tsl: inputs.tsl,
        axis: 'y',
      })
    : null

  // Compute MAWP at nominal thickness.
  const mawpC = ready
    ? computePipeSectionMAWP({
        t: inputs.nominalThickness,
        tsl: inputs.tsl,
        yieldStrength: inputs.yieldStrength,
        qualityFactor: inputs.qualityFactor,
        outerDiameter: inputs.outerDiameter,
        pressureCoefficient: inputs.pressureCoefficient,
        axis: 'x',
      })
    : null
  const mawpL = ready
    ? computePipeSectionMAWP({
        t: inputs.nominalThickness,
        tsl: inputs.tsl,
        yieldStrength: inputs.yieldStrength,
        qualityFactor: inputs.qualityFactor,
        outerDiameter: inputs.outerDiameter,
        pressureCoefficient: inputs.pressureCoefficient,
        axis: 'y',
      })
    : null

  const tMinCNum = tMinC?.toNumber(WORKING_UNIT_THICKNESS) ?? null
  const tMinLNum = tMinL?.toNumber(WORKING_UNIT_THICKNESS) ?? null
  const tMinNum =
    tMinCNum !== null && tMinLNum !== null ? Math.max(tMinCNum, tMinLNum) : null
  const tMin = toMeas(tMinNum, WORKING_UNIT_THICKNESS)

  const mawpCNum = mawpC?.toNumber(WORKING_UNIT_PRESSURE) ?? null
  const mawpLNum = mawpL?.toNumber(WORKING_UNIT_PRESSURE) ?? null
  const mawpNum =
    mawpCNum !== null && mawpLNum !== null ? Math.min(mawpCNum, mawpLNum) : null
  const mawp = toMeas(mawpNum, WORKING_UNIT_PRESSURE)

  const results: MinimumThicknessResult = {
    tMinC,
    tMinL,
    tMin,
    mawpC,
    mawpL,
    mawp,
  }

  // Physical / numeric validity beyond presence -- non-positive D / S_y / E /
  // t_nom or non-finite computed results would produce nonsense MAWP / t_min
  // and silently let the user advance.
  const invalidInputs: string[] = []
  if (ready) {
    if ((D_mm ?? 0) <= 0) invalidInputs.push('Outer diameter must be > 0.')
    if ((tNom_mm ?? 0) <= 0)
      invalidInputs.push('Nominal wall thickness must be > 0.')
    if ((P_MPa ?? 0) <= 0) invalidInputs.push('Design pressure must be > 0.')
    if ((Sy_MPa ?? 0) <= 0) invalidInputs.push('Yield strength must be > 0.')
    if ((E ?? 0) <= 0) invalidInputs.push('Quality factor must be > 0.')
    if ((Y ?? -1) < 0) invalidInputs.push('Pressure coefficient must be >= 0.')
    if ((tsl_mm ?? -1) < 0)
      invalidInputs.push('Longitudinal stress allowance must be >= 0.')
    if (tNom_mm !== null && tsl_mm !== null && tNom_mm - tsl_mm <= 0)
      invalidInputs.push(
        'Nominal thickness must exceed longitudinal stress allowance.'
      )
    if (tMinNum !== null && (!Number.isFinite(tMinNum) || tMinNum <= 0))
      invalidInputs.push('Computed t_min is not a valid positive value.')
    if (mawpNum !== null && (!Number.isFinite(mawpNum) || mawpNum <= 0))
      invalidInputs.push('Computed MAWP is not a valid positive value.')
  }

  let validation: StepValidationResult = undefined
  if (missingInputs.length > 0) {
    validation = {
      status: 'Error',
      message: `Missing inputs: ${missingInputs.join(', ')}.`,
    }
  } else if (invalidInputs.length > 0) {
    validation = {
      status: 'Error',
      message: `Invalid inputs: ${invalidInputs.join(' ')}`,
    }
  }

  return { validation, inputs, results, missingInputs }
}
